import {List, Map as IMap, Record, type RecordOf} from 'immutable';
import {type Json} from '@packages/utilities/json';
import {type CodeBlockRecord} from '@packages/systems/customCode/ui';
import {Component} from '@packages/systems/wfdl';
import {AvailableUtilityPages} from '@packages/domains/designer-workspace/page-management/data-types';

export const parseUtilKey = (utilKey: Json): null | AvailableUtilityPages =>
  utilKey === '401' ||
  utilKey === '404' ||
  utilKey === 'search' ||
  utilKey === 'ecommerce-checkout' ||
  utilKey === 'ecommerce-paypal-checkout' ||
  utilKey === 'ecommerce-confirmation' ||
  utilKey === 'usys-log-in' ||
  utilKey === 'usys-sign-up' ||
  utilKey === 'usys-reset-password' ||
  utilKey === 'usys-user-account' ||
  utilKey === 'usys-update-password' ||
  utilKey === 'usys-access-denied'
    ? utilKey
    : null;

/**
 * A generic type that can be used to create new page kinds.
 *
 * @example
 *
 * type MyPageKind = PageKind<'my-page-kind', {myCustomProperty: string}>;
 */
type PageKind<K = string, M = {[key: string]: unknown}> = RecordOf<{
  kind: K;
  meta: M;
}>;

/**
 * A `PageKind` representing a Component Page used within the Designer.
 */
export type ComponentPageKind = PageKind<
  'component',
  {
    componentId: Component.ComponentName;
  }
>;

/**
 * Union of all possible page kinds.
 * Currently this will only be `ComponentPageKind`, but future and existing
 * kinds of pages can be consolidated under this type.
 */
export type PageKindType = ComponentPageKind;

type PageRecordProps = {
  id: string;
  utilKey: AvailableUtilityPages | null | undefined;
  name: string;
  slug: string;
  description: string | null | undefined;
  parentId: string | null | undefined;
  siblingIndex: number;
  site: string;
  seoTitle: string;
  seoDesc: string;
  draft: boolean;
  branchId: string | null | undefined;
  /** Site-branch tree membership; unset on main and legacy page-branch rows. */
  branchRole: 'working' | 'base' | null | undefined;
  /**
   * Main SitePage this site-branch row forked from. Unset for branch-local
   * creates and for legacy page-branch / main rows.
   */
  branchMainPageId: string | null | undefined;
  canBranch: boolean;
  isStandaloneBranch: boolean;
  isStaticTemplate: boolean;
  duplicateOf: string;
  pageWasCreatedFromDuplicationFlow: boolean;
  ogTitle: string;
  ogTitleCopied: boolean;
  ogDesc: string;
  ogDescCopied: boolean;
  ogImages: List<any>;
  ogImageAssetId?: string | null;
  head: string;
  postBody: string;
  externalCode: List<CodeBlockRecord>;
  createdOn: string;
  isHome: boolean;
  dynCollection: string | null | undefined;
  type: string | null | undefined;
  generateRss: boolean | null | undefined;
  rssTitle: string;
  rssDesc: string;
  rssLink: string | null | undefined;
  rssTtl: string | null | undefined;
  rssItemTitle: string;
  rssItemTitleCopied: boolean;
  rssItemDesc: string;
  rssItemDescCopied: boolean;
  rssItemImage: null | string;
  rssItemPubDateSlug: string;
  searchExclude: boolean;
  searchTitle: string;
  searchTitleCopied: boolean;
  searchDescription: string;
  searchDescriptionCopied: boolean;
  searchImage: string;
  searchImageCopied: boolean;
  searchSnippetOverride: string;
  passwordProtected: boolean;
  password: string;
  groupAccess: List<string>;
  includeInSitemap: boolean;
  shouldPublish: boolean;
  localizedSettings?: PageLocalizedSettings;
  autoTranslatedSettings?: PageAutoTranslatedSettings;
  stylesVersion: number | null;
  dynItemId?: string | null;
  selectedStaticTemplate: string;
  createBuildModeBlankPage?: boolean; // FE only attribute, does not persist to the Page Record in Mongo
  canonicalUrl: string | null;
  pageKind: PageKindType | null | undefined;
  jsonLdSchema: string;
};

const defaultPageRecordProps: PageRecordProps = {
  // @ts-expect-error id is typed as string in PageRecordProps
  id: null as string,
  utilKey: null,
  name: '',
  slug: '',
  description: null,
  parentId: null,
  // @ts-expect-error siblingIndex is typed as number in PageRecordProps
  siblingIndex: null as number,
  site: '',
  seoTitle: '',
  seoDesc: '',
  draft: false,
  branchId: null,
  branchRole: null,
  branchMainPageId: null,
  canBranch: true,
  isStandaloneBranch: false,
  isStaticTemplate: false,
  duplicateOf: '',
  pageWasCreatedFromDuplicationFlow: false,
  ogTitle: '',
  ogTitleCopied: false,
  ogDesc: '',
  ogDescCopied: false,
  ogImages: List(),
  ogImageAssetId: null,
  head: '',
  postBody: '',
  externalCode: List(),
  createdOn: '',
  isHome: false,
  dynCollection: null,
  type: null,
  generateRss: null,
  rssTitle: '',
  rssDesc: '',
  rssLink: null,
  rssTtl: null,
  rssItemTitle: '',
  rssItemTitleCopied: false,
  rssItemDesc: '',
  rssItemDescCopied: false,
  rssItemImage: null,
  // rssItemPubDateSlug needs to have an initial value so that we don't
  // have false positive unsaved changes in legacy pages' settings
  rssItemPubDateSlug: 'published-on',
  searchExclude: false,
  searchTitle: '',
  searchTitleCopied: true,
  searchDescription: '',
  searchDescriptionCopied: true,
  searchImage: '',
  searchImageCopied: true,
  searchSnippetOverride: '',
  passwordProtected: false,
  password: '',
  groupAccess: List(),
  includeInSitemap: true,
  shouldPublish: true,

  localizedSettings: IMap<string, PageLocalizedSettingsForLocale>(),
  autoTranslatedSettings: IMap<string, PageAutoTranslatedSettingsForLocale>(),

  // HACK: Normally the code path where we need this is only on CSS sync,
  // but stylesVersion happens to get updated as well whenever a new page is created
  // (eg visiting utility page for the first time), and this number needs to stay
  // in sync with the backend, otherwise users will get annoying modal saying that
  // there is another instance of the designer opened.
  stylesVersion: null,
  dynItemId: null,
  selectedStaticTemplate: '',
  createBuildModeBlankPage: false, // FE only attribute, does not persist to the Page Record in Mongo
  canonicalUrl: null,
  pageKind: null,
  jsonLdSchema: '',
};

const PageRecord: Record.Factory<PageRecordProps> = Record(
  defaultPageRecordProps,
  'PageRecord'
);

export type PageRecordType = RecordOf<PageRecordProps>;

type PageListNodeProps = {
  id: string;
  name: string;
  type: string;
  slug: string;
  utilKey: AvailableUtilityPages | null | undefined;
  isExpanded: boolean;
  isSelected: boolean;
  /**
   * The depth of the page/folder within the overall page tree structure
   */
  treeDepth: number;
  childCount: number;
  dynCollection: null;
  passwordProtected: false;
  protectedAncestor: null;
  groupAccess: List<string>;
  draft: false;
  branchId: string | null | undefined;
  branchRole: 'working' | 'base' | null | undefined;
  canBranch: boolean;
  hasBranch: boolean;
  isStandaloneBranch: boolean;
  isStaticTemplate: boolean;
  isHome: boolean;
  isEcommerce: boolean;
  isValidDropLocation: boolean;
  localizedSettings: PageLocalizedSettings | null;
  shouldPublish: boolean;
  selectedItemId: string;
  parentId: string;
};

export type PageListNodeType = RecordOf<PageListNodeProps>;

const defaultPageListNodeProps: PageListNodeProps = {
  // @ts-expect-error id is typed as string in PageListNodeProps
  id: null as string,
  // @ts-expect-error name is typed as string in PageListNodeProps
  name: null as string,
  // @ts-expect-error id type typed as string in PageListNodeProps
  type: null as string,
  // @ts-expect-error id slug typed as string in PageListNodeProps
  slug: null as string,
  utilKey: null,

  isExpanded: false,
  isSelected: false,

  treeDepth: 1,
  childCount: 0,
  dynCollection: null,
  passwordProtected: false,
  protectedAncestor: null,
  groupAccess: List(),
  draft: false,
  branchId: null,
  branchRole: null,
  canBranch: true,
  hasBranch: false,
  isStandaloneBranch: false,
  isStaticTemplate: false,
  isHome: false,
  isEcommerce: false,
  isValidDropLocation: true,
  localizedSettings: null,
  shouldPublish: true,
  // @ts-expect-error selectedItemId is typed as string in PageListNodeProps
  selectedItemId: null as string,
  parentId: '',
};

/**
 * A specialized Record for accumulating only the data necessary for the
 * PageList to render.
 */
export const PageListNode: Record.Factory<PageListNodeProps> = Record(
  defaultPageListNodeProps,
  'PageListNode'
);

export default PageRecord;

export type StaticPagesType = IMap<string, PageRecordType>;
export type DynamicPagesType = IMap<string, PageRecordType>;
export type ComponentPagesType = IMap<string, PageRecordType>;

export type SyncingStateType = boolean;

export type PageBranchWarningModalStateType = RecordOf<{
  isModalOpened: boolean;
  userId: null | string;
}>;

export type CMSFilterSource = 'context bar' | 'pages panel';

export type PageState = RecordOf<{
  staticPages: StaticPagesType;
  dynamicPages: DynamicPagesType;
  componentPages: ComponentPagesType;
  isSyncing: SyncingStateType;
  pageBranchWarningModalState: PageBranchWarningModalStateType;
  mintedSearchResultsPage: PageRecordType | null;
  /**
   * Stores the state of the cms filter, the page id and source - pages panel or context bar
   */
  cmsFilterState?: RecordOf<{
    pageIdForContextBar: string | null;
    pageIdForPagesPanel: string | null;
  }>;
}>;

export type PageLocalizedSettings = IMap<
  // locale id
  string,
  PageLocalizedSettingsForLocale
>;

// settings object, e.g. { title: 'Some Title', slug: 'some-slug', ogDescCopied: false, … }
export type PageLocalizedSettingsForLocale = IMap<string, any>;

/**
 * Machine translations of page metadata. Read-only in the Designer and absent
 * from PageClient's `intoJSON` allowlist, so a save can't echo it back.
 */
export type PageAutoTranslatedSettings = IMap<
  // locale id
  string,
  PageAutoTranslatedSettingsForLocale
>;

// field name -> {v: translation, src: fingerprint of the primary it came from}
export type PageAutoTranslatedSettingsForLocale = IMap<string, unknown>;
